import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useAuth, getAuthenticatedMediaUrl } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Briefcase, Plus, Search, X, Loader2, Paperclip, Trash2, Link2, ListChecks,
  LayoutGrid, List as ListIcon, Users as UsersIcon, FileText,
} from "lucide-react";

// ── Enums + display ──────────────────────────────────────────────────────────
const INV_STATUSES = [
  { value: "open", label: "Open" },
  { value: "active", label: "Active" },
  { value: "follow_up", label: "Follow-up" },
  { value: "closed", label: "Closed" },
];
const TASK_STATUSES = [
  { value: "to_do", label: "To Do" },
  { value: "in_progress", label: "In Progress" },
  { value: "follow_up", label: "Follow-up" },
  { value: "done", label: "Done" },
];
const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const STATUS_CLASS = {
  open: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  active: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  follow_up: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  closed: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};
const TASK_STATUS_CLASS = {
  to_do: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  in_progress: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  follow_up: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  done: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
};
const PRIORITY_CLASS = {
  low: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  medium: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  high: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  urgent: "bg-red-500/15 text-red-400 border-red-500/30",
};

const labelFor = (arr, v) => arr.find((x) => x.value === v)?.label || v;
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
};

export default function Investigations() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState("list"); // list | board | mine
  const [investigations, setInvestigations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [filters, setFilters] = useState({ search: "", status: "all", priority: "all", assignee: "all" });

  const [members, setMembers] = useState([]);
  const membersMap = useMemo(() => {
    const m = {};
    members.forEach((x) => { m[x.user_id] = x; });
    return m;
  }, [members]);
  const memberName = useCallback((id) => membersMap[id]?.name || (id ? "Unknown" : ""), [membersMap]);

  // Incidents (for link picker + linked-label display)
  const [allIncidents, setAllIncidents] = useState([]);
  const incidentMap = useMemo(() => {
    const m = {};
    allIncidents.forEach((i) => { m[i.incident_id] = i; });
    return m;
  }, [allIncidents]);

  // Create
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ title: "", status: "open", priority: "medium", lead_user_id: "", description: "" });
  const [creating, setCreating] = useState(false);

  // Detail
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailEdit, setDetailEdit] = useState({ title: "", description: "" });

  // Task editor
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: "", status: "to_do", priority: "medium", assignee_user_ids: [], notes: "" });
  const [taskNewSubtasks, setTaskNewSubtasks] = useState([]); // for new-task only
  const [taskSubtaskDraft, setTaskSubtaskDraft] = useState("");

  // Link incident
  const [linkQuery, setLinkQuery] = useState("");

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);

  // My Tasks
  const [myTasks, setMyTasks] = useState([]);

  const [uploading, setUploading] = useState(false);

  // ── Fetchers ───────────────────────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.status !== "all") params.append("status", filters.status);
      if (filters.priority !== "all") params.append("priority", filters.priority);
      if (filters.assignee !== "all") params.append("assignee", filters.assignee);
      const res = await axios.get(`${API}/investigations?${params}`, { withCredentials: true });
      setInvestigations(res.data || []);
      setAccessDenied(false);
    } catch (err) {
      if (err.response?.status === 403) { setAccessDenied(true); }
      else { toast.error("Failed to load investigations"); }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/investigations/members`, { withCredentials: true });
      setMembers(res.data || []);
    } catch { /* 403 handled by list */ }
  }, []);

  const fetchIncidents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/incidents?limit=500`, { withCredentials: true });
      setAllIncidents(res.data || []);
    } catch { /* incidents access may differ; link picker just won't show options */ }
  }, []);

  const fetchMyTasks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/investigations/my-tasks`, { withCredentials: true });
      setMyTasks(res.data || []);
    } catch { /* ignore */ }
  }, []);

  const fetchDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/investigations/${id}`, { withCredentials: true });
      setDetail(res.data);
      setDetailEdit({ title: res.data.title || "", description: res.data.description || "" });
    } catch (err) {
      toast.error("Failed to load investigation");
      setDetailId(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { fetchMembers(); fetchIncidents(); }, [fetchMembers, fetchIncidents]);
  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { if (view === "mine") fetchMyTasks(); }, [view, fetchMyTasks]);
  useEffect(() => { if (detailId) fetchDetail(detailId); else setDetail(null); }, [detailId, fetchDetail]);

  // ── Mutations ────────────────────────────────────────────────────────────────
  const createInvestigation = async () => {
    if (!createForm.title.trim()) { toast.error("Title is required"); return; }
    setCreating(true);
    try {
      const payload = {
        title: createForm.title.trim(),
        status: createForm.status,
        priority: createForm.priority,
        description: createForm.description || null,
        lead_user_id: createForm.lead_user_id || null,
      };
      const res = await axios.post(`${API}/investigations`, payload, { withCredentials: true });
      toast.success("Investigation created");
      setCreateOpen(false);
      setCreateForm({ title: "", status: "open", priority: "medium", lead_user_id: "", description: "" });
      fetchList();
      setDetailId(res.data.investigation_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  // Patch an investigation; refresh detail + list row.
  const patchInvestigation = async (id, fields, { silent } = {}) => {
    try {
      const res = await axios.put(`${API}/investigations/${id}`, fields, { withCredentials: true });
      if (detailId === id) {
        setDetail(res.data);
        setDetailEdit({ title: res.data.title || "", description: res.data.description || "" });
      }
      setInvestigations((prev) => prev.map((inv) => inv.investigation_id === id ? {
        ...inv,
        ...("status" in fields ? { status: fields.status } : {}),
        ...("priority" in fields ? { priority: fields.priority } : {}),
        ...("lead_user_id" in fields ? { lead_user_id: fields.lead_user_id } : {}),
        ...("title" in fields ? { title: fields.title } : {}),
      } : inv));
      if (!silent) toast.success("Saved");
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
      throw err;
    }
  };

  const saveDetailFields = async () => {
    if (!detail) return;
    if (!detailEdit.title.trim()) { toast.error("Title is required"); return; }
    await patchInvestigation(detail.investigation_id, { title: detailEdit.title.trim(), description: detailEdit.description || null }, { silent: true });
  };

  const changeStatus = async (id, status) => {
    await patchInvestigation(id, { status }, { silent: true });
  };

  const toggleAssignee = async (uid) => {
    if (!detail) return;
    const cur = detail.assignee_user_ids || [];
    const next = cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid];
    await patchInvestigation(detail.investigation_id, { assignee_user_ids: next }, { silent: true });
  };

  const deleteInvestigation = async (id) => {
    try {
      await axios.delete(`${API}/investigations/${id}`, { withCredentials: true });
      toast.success("Investigation deleted");
      setDeleteTarget(null);
      setDetailId(null);
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  // Tasks
  const openNewTask = () => {
    setEditingTaskId(null);
    setTaskForm({ title: "", status: "to_do", priority: "medium", assignee_user_ids: [], notes: "" });
    setTaskNewSubtasks([]);
    setTaskSubtaskDraft("");
    setTaskDialogOpen(true);
  };
  const openEditTask = (t) => {
    setEditingTaskId(t.task_id);
    setTaskForm({
      title: t.title || "", status: t.status || "to_do", priority: t.priority || "medium",
      assignee_user_ids: t.assignee_user_ids || [], notes: t.notes || "",
    });
    setTaskNewSubtasks([]);
    setTaskSubtaskDraft("");
    setTaskDialogOpen(true);
  };
  const saveTask = async () => {
    if (!detail) return;
    if (!taskForm.title.trim()) { toast.error("Task title required"); return; }
    try {
      if (editingTaskId) {
        const res = await axios.put(`${API}/investigations/${detail.investigation_id}/tasks/${editingTaskId}`, {
          title: taskForm.title.trim(), status: taskForm.status, priority: taskForm.priority,
          assignee_user_ids: taskForm.assignee_user_ids, notes: taskForm.notes || null,
        }, { withCredentials: true });
        setDetail(res.data);
      } else {
        await axios.post(`${API}/investigations/${detail.investigation_id}/tasks`, {
          title: taskForm.title.trim(), status: taskForm.status, priority: taskForm.priority,
          assignee_user_ids: taskForm.assignee_user_ids, notes: taskForm.notes || null,
          subtasks: taskNewSubtasks.map((t) => ({ text: t })),
        }, { withCredentials: true });
        await fetchDetail(detail.investigation_id);
      }
      setTaskDialogOpen(false);
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save task");
    }
  };
  const changeTaskStatus = async (taskId, status) => {
    if (!detail) return;
    try {
      const res = await axios.put(`${API}/investigations/${detail.investigation_id}/tasks/${taskId}`, { status }, { withCredentials: true });
      setDetail(res.data);
      fetchList();
    } catch { toast.error("Failed to update task"); }
  };
  const deleteTask = async (taskId) => {
    if (!detail) return;
    try {
      const res = await axios.delete(`${API}/investigations/${detail.investigation_id}/tasks/${taskId}`, { withCredentials: true });
      setDetail(res.data);
      fetchList();
    } catch { toast.error("Failed to delete task"); }
  };
  const toggleSubtask = async (taskId, subtaskId, done) => {
    if (!detail) return;
    try {
      const res = await axios.put(`${API}/investigations/${detail.investigation_id}/tasks/${taskId}/subtasks/${subtaskId}`, { done }, { withCredentials: true });
      setDetail(res.data);
    } catch { toast.error("Failed to update subtask"); }
  };

  // Linked incidents
  const linkIncident = async (incidentId) => {
    if (!detail) return;
    try {
      const res = await axios.post(`${API}/investigations/${detail.investigation_id}/link-incident`, { incident_id: incidentId }, { withCredentials: true });
      setDetail(res.data);
      setLinkQuery("");
      fetchList();
    } catch { toast.error("Failed to link incident"); }
  };
  const unlinkIncident = async (incidentId) => {
    if (!detail) return;
    try {
      const res = await axios.delete(`${API}/investigations/${detail.investigation_id}/link-incident/${incidentId}`, { withCredentials: true });
      setDetail(res.data);
      fetchList();
    } catch { toast.error("Failed to unlink incident"); }
  };

  // Attachments (investigation-level)
  const uploadAttachments = async (e) => {
    const files = e.target.files;
    if (!files || !files.length || !detail) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await axios.post(`${API}/upload`, fd, {
          withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
        });
        await axios.post(`${API}/investigations/${detail.investigation_id}/attachments`, {
          file_id: up.data.file_id, url: up.data.url, filename: file.name,
          content_type: file.type || null, size: file.size || null,
        }, { withCredentials: true });
      }
      await fetchDetail(detail.investigation_id);
      fetchList();
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const deleteAttachment = async (fileId) => {
    if (!detail) return;
    try {
      await axios.delete(`${API}/investigations/${detail.investigation_id}/attachments/${fileId}`, { withCredentials: true });
      await fetchDetail(detail.investigation_id);
      fetchList();
    } catch { toast.error("Failed to remove attachment"); }
  };

  // ── Reusable bits (plain render fns, not nested components, so text inputs
  //    elsewhere don't remount/lose focus on every parent render) ─────────────
  const memberPicker = (selected, onToggle, placeholder = "Assign…") => (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-xs">
          <UsersIcon className="w-3.5 h-3.5 mr-1" />
          {selected.length ? `${selected.length} assigned` : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
        <div className="max-h-60 overflow-y-auto space-y-1">
          {members.length === 0 && <div className="text-xs text-slate-500 p-2">No members</div>}
          {members.map((m) => (
            <label key={m.user_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer">
              <Checkbox checked={selected.includes(m.user_id)} onCheckedChange={() => onToggle(m.user_id)} />
              <span className="text-sm">{m.name}{m.linked_unit ? <span className="text-slate-500"> ({m.linked_unit})</span> : null}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );

  const assigneeChips = (ids) => (
    <div className="flex flex-wrap gap-1">
      {(ids || []).map((id) => (
        <Badge key={id} variant="outline" className="text-[10px] bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700">{memberName(id)}</Badge>
      ))}
    </div>
  );

  // ── Access gate ──────────────────────────────────────────────────────────────
  if (accessDenied) {
    return (
      <div className="p-8 text-center text-slate-500 dark:text-slate-400">
        <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p>You don't have access to Investigations.</p>
      </div>
    );
  }

  const linkResults = linkQuery.trim()
    ? allIncidents.filter((i) => {
        const q = linkQuery.toLowerCase();
        return (i.incident_id || "").toLowerCase().includes(q) || (i.headline || "").toLowerCase().includes(q) || (i.address || "").toLowerCase().includes(q);
      }).filter((i) => !(detail?.linked_incident_ids || []).includes(i.incident_id)).slice(0, 12)
    : [];

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="p-3 sm:p-4 space-y-4 text-slate-900 dark:text-slate-100">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-blue-500" />
          <h1 className="text-xl font-bold">Investigations</h1>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="btn-primary">
          <Plus className="w-4 h-4 mr-1" /> New Investigation
        </Button>
      </div>

      {/* View tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 dark:border-slate-800">
        {[
          { k: "list", label: "List", icon: ListIcon },
          { k: "board", label: "Board", icon: LayoutGrid },
          { k: "mine", label: "My Tasks", icon: ListChecks },
        ].map((t) => (
          <button key={t.k} onClick={() => setView(t.k)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              view === t.k ? "border-blue-500 text-blue-600 dark:text-blue-400" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
            }`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Filters (list + board) */}
      {view !== "mine" && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              placeholder="Search title or description…" className="pl-9 input-dark" />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
            <SelectTrigger className="w-36 input-dark"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {INV_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.priority} onValueChange={(v) => setFilters((f) => ({ ...f, priority: v }))}>
            <SelectTrigger className="w-32 input-dark"><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.assignee} onValueChange={(v) => setFilters((f) => ({ ...f, assignee: v }))}>
            <SelectTrigger className="w-40 input-dark"><SelectValue placeholder="Assignee" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Anyone</SelectItem>
              {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {loading && view !== "mine" ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <>
          {view === "list" && listView()}
          {view === "board" && boardView()}
          {view === "mine" && myTasksView()}
        </>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <DialogHeader><DialogTitle>New Investigation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input value={createForm.title} onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))} className="input-dark" placeholder="Case title…" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Status</Label>
                <Select value={createForm.status} onValueChange={(v) => setCreateForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="input-dark"><SelectValue /></SelectTrigger>
                  <SelectContent>{INV_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={createForm.priority} onValueChange={(v) => setCreateForm((f) => ({ ...f, priority: v }))}>
                  <SelectTrigger className="input-dark"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Lead</Label>
              <Select value={createForm.lead_user_id || "none"} onValueChange={(v) => setCreateForm((f) => ({ ...f, lead_user_id: v === "none" ? "" : v }))}>
                <SelectTrigger className="input-dark"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={createForm.description} onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))} className="input-dark" placeholder="What is this case about?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createInvestigation} disabled={creating} className="btn-primary">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detailId} onOpenChange={(o) => { if (!o) setDetailId(null); }}>
        <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          {detailLoading || !detail ? (
            <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            detailBody()
          )}
        </DialogContent>
      </Dialog>

      {/* Task editor dialog */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <DialogHeader><DialogTitle>{editingTaskId ? "Edit Task" : "Add Task"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input value={taskForm.title} onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))} className="input-dark" placeholder="Task…" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Status</Label>
                <Select value={taskForm.status} onValueChange={(v) => setTaskForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="input-dark"><SelectValue /></SelectTrigger>
                  <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={taskForm.priority} onValueChange={(v) => setTaskForm((f) => ({ ...f, priority: v }))}>
                  <SelectTrigger className="input-dark"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Assignees</Label>
              <div className="mt-1">
                {memberPicker(taskForm.assignee_user_ids,
                  (uid) => setTaskForm((f) => ({ ...f, assignee_user_ids: f.assignee_user_ids.includes(uid) ? f.assignee_user_ids.filter((x) => x !== uid) : [...f.assignee_user_ids, uid] })))}
                <div className="mt-1">{assigneeChips(taskForm.assignee_user_ids)}</div>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={taskForm.notes} onChange={(e) => setTaskForm((f) => ({ ...f, notes: e.target.value }))} className="input-dark" />
            </div>
            {!editingTaskId && (
              <div>
                <Label>Subtasks (checklist)</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={taskSubtaskDraft} onChange={(e) => setTaskSubtaskDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && taskSubtaskDraft.trim()) { setTaskNewSubtasks((s) => [...s, taskSubtaskDraft.trim()]); setTaskSubtaskDraft(""); } }}
                    className="input-dark" placeholder="Add a checklist item + Enter" />
                </div>
                <div className="mt-2 space-y-1">
                  {taskNewSubtasks.map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-slate-100 dark:bg-slate-800 rounded px-2 py-1">
                      <span>{s}</span>
                      <button onClick={() => setTaskNewSubtasks((arr) => arr.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveTask} className="btn-primary">{editingTaskId ? "Save" : "Add Task"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete investigation?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the case and its tasks. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteInvestigation(deleteTarget)} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  // ── Render functions (invoked directly, e.g. {listView()}, not as <X/> —
  //    keeps form inputs from remounting on each parent render) ────────────────
  function listView() {
    if (!investigations.length) return emptyState();
    return (
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400">
            <tr>
              <th className="text-left font-medium px-3 py-2">Title</th>
              <th className="text-left font-medium px-3 py-2">Status</th>
              <th className="text-left font-medium px-3 py-2">Priority</th>
              <th className="text-left font-medium px-3 py-2">Lead</th>
              <th className="text-left font-medium px-3 py-2">Incidents</th>
              <th className="text-left font-medium px-3 py-2">Tasks</th>
              <th className="text-left font-medium px-3 py-2">Updated</th>
            </tr>
          </thead>
          <tbody>
            {investigations.map((inv) => (
              <tr key={inv.investigation_id} onClick={() => setDetailId(inv.investigation_id)}
                className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer">
                <td className="px-3 py-2 font-medium">{inv.title}</td>
                <td className="px-3 py-2"><Badge variant="outline" className={`text-[10px] ${STATUS_CLASS[inv.status]}`}>{labelFor(INV_STATUSES, inv.status)}</Badge></td>
                <td className="px-3 py-2"><Badge variant="outline" className={`text-[10px] ${PRIORITY_CLASS[inv.priority]}`}>{labelFor(PRIORITIES, inv.priority)}</Badge></td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{inv.lead_user_id ? memberName(inv.lead_user_id) : <span className="text-slate-400">—</span>}</td>
                <td className="px-3 py-2 text-slate-500">{inv.linked_incident_count || 0}</td>
                <td className="px-3 py-2 text-slate-500">{inv.open_task_count || 0}/{inv.task_count || 0}</td>
                <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{fmtDate(inv.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function boardView() {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {INV_STATUSES.map((col) => {
          const cards = investigations.filter((i) => i.status === col.value);
          return (
            <div key={col.value} className="bg-slate-100 dark:bg-slate-800/40 rounded-lg p-2 min-h-[120px]">
              <div className="flex items-center justify-between px-1 pb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{col.label}</span>
                <span className="text-xs text-slate-400">{cards.length}</span>
              </div>
              <div className="space-y-2">
                {cards.map((inv) => (
                  <Card key={inv.investigation_id} className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 cursor-pointer hover:border-blue-400 dark:hover:border-blue-600"
                    onClick={() => setDetailId(inv.investigation_id)}>
                    <CardContent className="p-3 space-y-2">
                      <div className="font-medium text-sm">{inv.title}</div>
                      <div className="flex items-center gap-1 flex-wrap">
                        <Badge variant="outline" className={`text-[10px] ${PRIORITY_CLASS[inv.priority]}`}>{labelFor(PRIORITIES, inv.priority)}</Badge>
                        {inv.linked_incident_count > 0 && <Badge variant="outline" className="text-[10px] text-slate-500"><Link2 className="w-3 h-3 mr-0.5" />{inv.linked_incident_count}</Badge>}
                        {inv.task_count > 0 && <Badge variant="outline" className="text-[10px] text-slate-500"><ListChecks className="w-3 h-3 mr-0.5" />{inv.open_task_count}/{inv.task_count}</Badge>}
                      </div>
                      {inv.lead_user_id && <div className="text-xs text-slate-500">Lead: {memberName(inv.lead_user_id)}</div>}
                      <div onClick={(e) => e.stopPropagation()}>
                        <Select value={inv.status} onValueChange={(v) => changeStatus(inv.investigation_id, v)}>
                          <SelectTrigger className="h-7 text-xs input-dark"><SelectValue /></SelectTrigger>
                          <SelectContent>{INV_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>Move to {s.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {cards.length === 0 && <div className="text-xs text-slate-400 px-1 py-2">No cases</div>}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function myTasksView() {
    if (!myTasks.length) return <div className="text-center py-16 text-slate-400">No tasks assigned to you.</div>;
    const groups = TASK_STATUSES.map((s) => ({ ...s, items: myTasks.filter((t) => (t.status || "to_do") === s.value) }));
    return (
      <div className="space-y-4">
        {groups.filter((g) => g.items.length).map((g) => (
          <div key={g.value}>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">{g.label} ({g.items.length})</div>
            <div className="space-y-1.5">
              {g.items.map((t) => (
                <div key={`${t.investigation_id}-${t.task_id}`} onClick={() => setDetailId(t.investigation_id)}
                  className="flex items-center justify-between gap-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 cursor-pointer hover:border-blue-400 dark:hover:border-blue-600">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{t.title}</div>
                    <div className="text-xs text-slate-500 truncate">{t.investigation_title}</div>
                  </div>
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${TASK_STATUS_CLASS[t.status]}`}>{labelFor(TASK_STATUSES, t.status)}</Badge>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  function emptyState() {
    return (
      <div className="text-center py-16 text-slate-400">
        <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p>No investigations yet.</p>
        <Button onClick={() => setCreateOpen(true)} variant="outline" className="mt-3"><Plus className="w-4 h-4 mr-1" /> New Investigation</Button>
      </div>
    );
  }

  function detailBody() {
    const isAdmin = user?.role === "admin";
    const tasks = [...(detail.tasks || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return (
      <div className="space-y-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-blue-500" />
            <Input value={detailEdit.title} onChange={(e) => setDetailEdit((d) => ({ ...d, title: e.target.value }))} onBlur={saveDetailFields}
              className="input-dark font-semibold text-base h-9" />
          </DialogTitle>
        </DialogHeader>

        {/* Top controls */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={detail.status} onValueChange={(v) => changeStatus(detail.investigation_id, v)}>
              <SelectTrigger className="input-dark h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{INV_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Priority</Label>
            <Select value={detail.priority} onValueChange={(v) => patchInvestigation(detail.investigation_id, { priority: v }, { silent: true })}>
              <SelectTrigger className="input-dark h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Lead</Label>
            <Select value={detail.lead_user_id || "none"} onValueChange={(v) => patchInvestigation(detail.investigation_id, { lead_user_id: v === "none" ? null : v }, { silent: true })}>
              <SelectTrigger className="input-dark h-8"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Assignees</Label>
            <div>{memberPicker(detail.assignee_user_ids || [], toggleAssignee)}</div>
          </div>
        </div>
        {assigneeChips(detail.assignee_user_ids)}

        {/* Description */}
        <div>
          <Label className="text-xs">Description / Notes</Label>
          <Textarea value={detailEdit.description} onChange={(e) => setDetailEdit((d) => ({ ...d, description: e.target.value }))} onBlur={saveDetailFields} className="input-dark" />
        </div>

        {/* Linked incidents */}
        <div className="border-t border-slate-200 dark:border-slate-800 pt-3">
          <div className="flex items-center gap-1.5 mb-2 text-sm font-semibold"><Link2 className="w-4 h-4" /> Linked Incidents</div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(detail.linked_incident_ids || []).map((id) => {
              const inc = incidentMap[id];
              return (
                <Badge key={id} variant="outline" className="text-xs bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 flex items-center gap-1">
                  <button onClick={() => navigate(`/incidents/${id}`)} className="hover:underline">
                    {inc ? `${id} · ${inc.headline}` : `${id} (removed)`}
                  </button>
                  <button onClick={() => unlinkIncident(id)} className="text-slate-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                </Badge>
              );
            })}
            {!(detail.linked_incident_ids || []).length && <span className="text-xs text-slate-400">None linked</span>}
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={linkQuery} onChange={(e) => setLinkQuery(e.target.value)} className="pl-9 input-dark h-8" placeholder="Search incidents to link…" />
          </div>
          {linkResults.length > 0 && (
            <div className="mt-1 max-h-44 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg">
              {linkResults.map((i) => (
                <button key={i.incident_id} onClick={() => linkIncident(i.incident_id)}
                  className="w-full text-left px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 border-b border-slate-100 dark:border-slate-800 last:border-0">
                  <div className="text-sm font-medium">{i.incident_id} · {i.headline}</div>
                  <div className="text-xs text-slate-500">{i.address}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Attachments */}
        <div className="border-t border-slate-200 dark:border-slate-800 pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 text-sm font-semibold"><Paperclip className="w-4 h-4" /> Attachments</div>
            <label className="cursor-pointer">
              <input type="file" multiple className="hidden" onChange={uploadAttachments} />
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Upload
              </span>
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            {(detail.attachments || []).map((a) => {
              const isImg = (a.content_type || "").startsWith("image/");
              const base = `${API.replace("/api", "")}${a.url}`;
              return (
                <div key={a.file_id} className="relative group">
                  {isImg ? (
                    <img src={getAuthenticatedMediaUrl(`${base}/thumb?size=96`)} alt={a.filename}
                      onClick={() => window.open(getAuthenticatedMediaUrl(base), "_blank")}
                      className="w-16 h-16 object-cover rounded border border-slate-300 dark:border-slate-700 cursor-pointer"
                      onError={(e) => { e.currentTarget.src = getAuthenticatedMediaUrl(base); }} />
                  ) : (
                    <button onClick={() => window.open(getAuthenticatedMediaUrl(base), "_blank")}
                      className="w-16 h-16 flex flex-col items-center justify-center gap-1 rounded border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-slate-500 text-[9px] p-1 overflow-hidden">
                      <FileText className="w-5 h-5" /><span className="truncate w-full text-center">{a.filename}</span>
                    </button>
                  )}
                  <button onClick={() => deleteAttachment(a.file_id)}
                    className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-2.5 h-2.5" /></button>
                </div>
              );
            })}
            {!(detail.attachments || []).length && <span className="text-xs text-slate-400">No files</span>}
          </div>
        </div>

        {/* Tasks */}
        <div className="border-t border-slate-200 dark:border-slate-800 pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 text-sm font-semibold"><ListChecks className="w-4 h-4" /> Tasks ({tasks.length})</div>
            <Button size="sm" variant="outline" onClick={openNewTask} className="h-7 text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Add Task</Button>
          </div>
          <div className="space-y-2">
            {tasks.map((t) => (
              <div key={t.task_id} className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 rounded-lg p-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <button onClick={() => openEditTask(t)} className="text-sm font-medium text-left hover:underline">{t.title}</button>
                    {t.notes && <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{t.notes}</div>}
                    <div className="flex items-center gap-1 flex-wrap mt-1">
                      <Badge variant="outline" className={`text-[10px] ${PRIORITY_CLASS[t.priority]}`}>{labelFor(PRIORITIES, t.priority)}</Badge>
                      {assigneeChips(t.assignee_user_ids)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Select value={t.status} onValueChange={(v) => changeTaskStatus(t.task_id, v)}>
                      <SelectTrigger className={`h-7 text-[11px] w-28 input-dark`}><SelectValue /></SelectTrigger>
                      <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <button onClick={() => deleteTask(t.task_id)} className="text-slate-400 hover:text-red-500 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                {(t.subtasks || []).length > 0 && (
                  <div className="mt-2 space-y-1 pl-1">
                    {t.subtasks.map((s) => (
                      <label key={s.subtask_id} className="flex items-center gap-2 text-xs cursor-pointer">
                        <Checkbox checked={s.done} onCheckedChange={(c) => toggleSubtask(t.task_id, s.subtask_id, !!c)} />
                        <span className={s.done ? "line-through text-slate-400" : ""}>{s.text}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {!tasks.length && <div className="text-xs text-slate-400">No tasks yet.</div>}
          </div>
        </div>

        {/* Footer actions */}
        <DialogFooter className="border-t border-slate-200 dark:border-slate-800 pt-3">
          {isAdmin && (
            <Button variant="outline" onClick={() => setDeleteTarget(detail.investigation_id)} className="text-red-500 border-red-300 dark:border-red-900 hover:bg-red-50 dark:hover:bg-red-950 mr-auto">
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={() => setDetailId(null)}>Close</Button>
        </DialogFooter>
      </div>
    );
  }
}
